import { PropsWithChildren, ReactNode } from "react"
import { makeStyles } from "tss-react/mui"
import { Breakpoint, Grid } from "@mui/material"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { StandardButton, StandardButtonColor } from "../buttons/StandardButton"
import { StandardLoadingButton } from "../buttons/LoadingButton"
import { StandardDialog } from "./StandardDialog"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"

type ConfirmModalProps = Readonly<
    PropsWithChildren<{
        open: boolean
        buttonSubmitDisabled?: boolean
        buttonSubmitLoading?: boolean
        onClose: () => void
        title: ReactNode
        message?: ReactNode
        handleClick(): void
        buttonLabelSubmit: string
        buttonColorSubmit?: StandardButtonColor
        buttonLabelCancel?: string
        maxWidthProp?: Breakpoint
        classNameOverride?: string
        autoCloseAfterSubmit?: boolean
        hideCloseIcon?: boolean
        disableScrollLock?: boolean
    }>
>

const useStyles = makeStyles()((theme) => ({
    dialogPaper: {
        minHeight: "320px",
        justifyContent: "center",
    },
    container: {
        gap: "2rem",
        padding: "0rem 4rem",
    },
    dialogTitle: {
        textAlign: "center",
        fontWeight: 600,
        paddingBottom: "0.75rem",
        // color: "#334155",
        color: tailwindPalette.slate[700],
    },
    button: {
        borderRadius: "0.5rem",
        paddingLeft: "2rem",
        paddingRight: "2rem",
    },
    successButton: {
        // background: "#EF4444",
        background: tailwindPalette.red[500],
    },
}))

export function ConfirmModal({
    open,
    onClose,
    title,
    message,
    handleClick,
    buttonLabelSubmit,
    buttonColorSubmit = "error",
    buttonLabelCancel,
    buttonSubmitDisabled = false,
    buttonSubmitLoading = false,
    children,
    maxWidthProp = "sm",
    classNameOverride = "",
    autoCloseAfterSubmit = true,
    hideCloseIcon = true,
    disableScrollLock = false,
}: ConfirmModalProps) {
    const { classes, cx } = useStyles()

    const { l10n } = useAppLocalization()

    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            maxWidthProp={maxWidthProp}
            dialogTitle={title}
            subtitle={message}
            // slotProps={{ paper: { className: cx(classNameOverride) } }}
            slotProps={{ paper: { className: cx(classes.dialogPaper, classNameOverride) } }} // check on test
            hideCloseIcon={hideCloseIcon}
            disableScrollLock={disableScrollLock}
        >
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                direction="column"
                wrap="nowrap"
                className={classes.container}
            >
                {children}
                <Grid container direction="row" wrap="nowrap" alignItems="center" justifyContent="center" gap={1.25}>
                    <StandardButton
                        color="grey"
                        variant="outlined"
                        onClick={onClose}
                        size="medium"
                        fullWidth={false}
                        updateDefaultClasses={classes.button}
                    >
                        {!!buttonLabelCancel ? buttonLabelCancel : l10n.getString("delete-confirm-modal-button-cancel")}
                    </StandardButton>
                    <StandardLoadingButton
                        color={buttonColorSubmit}
                        onClick={() => {
                            handleClick()
                            autoCloseAfterSubmit && onClose()
                        }}
                        disabled={buttonSubmitDisabled}
                        loading={buttonSubmitLoading}
                        size="medium"
                        fullWidth={false}
                        className={cx({
                            [classes.successButton]: buttonColorSubmit === "error",
                        })}
                        updateDefaultClasses={classes.button}
                    >
                        {buttonLabelSubmit}
                    </StandardLoadingButton>
                </Grid>
            </Grid>
        </StandardDialog>
    )
}
